import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import { makeFormatters } from "./format";
import {
  SnapshotContext,
  type SnapshotContextValue,
} from "./snapshot-context";

export function SnapshotProvider({ children }: { children: React.ReactNode }) {
  const query = useQuery({
    queryKey: ["snapshot"],
    queryFn: () => api.snapshot(),
    refetchInterval: 60_000,
  });

  const value = useMemo<SnapshotContextValue | null>(() => {
    if (!query.data) return null;
    const now = new Date(query.data.now);
    return { data: query.data, now, fmt: makeFormatters(now) };
  }, [query.data]);

  if (query.isLoading) {
    return (
      <div style={fullCenter}>
        <span style={{ color: "var(--muted-foreground)", fontSize: 13 }}>
          Loading Status Watch…
        </span>
      </div>
    );
  }

  if (query.isError || !value) {
    return (
      <div style={fullCenter}>
        <div style={{ textAlign: "center", maxWidth: 420 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
            Couldn’t reach the Status Watch API
          </div>
          <div style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
            {(query.error as Error | undefined)?.message ?? "Unknown error"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <SnapshotContext.Provider value={value}>
      {children}
    </SnapshotContext.Provider>
  );
}

const fullCenter: React.CSSProperties = {
  minHeight: "100vh",
  display: "grid",
  placeItems: "center",
  background: "var(--background)",
};
